import { shade } from '../pixel.js';
import type { PixelCanvas } from '../pixel.js';
import { box, drawBox, drawBoxes, drawCylinder, faceQuad, faces, frontFace, iso } from './iso.js';
import type { FurniDrawer } from './iso.js';

/*
 * Pet furniture: beds, bowls, toys, the kennel, the pet shop counter and the
 * dig spots of the dog run. Owned by the pets feature.
 */

function diamond(pc: PixelCanvas, r: number, color: string): void {
  pc.poly([iso(-r, -r), iso(r, -r), iso(r, r), iso(-r, r)], color);
}

/** A paw print, a few pixels across, centred on screen point (x, y). */
function paw(pc: PixelCanvas, x: number, y: number, color: string): void {
  pc.rect(x - 1, y, 3, 2, color);
  pc.px(x - 2, y - 2, color);
  pc.px(x, y - 3, color);
  pc.px(x + 2, y - 2, color);
}

const drawShopCounter: FurniDrawer = (pc, rotation) => {
  drawBox(pc, box(-0.46, 0.46, -0.4, 0.4, 0, 32, faces('#3d9e8c')));
  drawBox(pc, box(-0.49, 0.49, -0.43, 0.43, 32, 36, faces('#c98f58')));
  const face = frontFace(rotation) ?? 'left';
  const at = face === 'left' ? 0.4 : 0.46;
  faceQuad(pc, face, at, -0.3, 0.3, 10, 26, '#fffaf0');
  const mid = face === 'left' ? iso(0, at, 18) : iso(at, 0, 18);
  paw(pc, mid.x, mid.y, '#d9433b');
  // A bag of kibble and a little fish bowl on the counter.
  drawBox(pc, box(-0.35, -0.12, -0.3, -0.08, 36, 50, faces('#e8a33c')));
  const bag = iso(-0.23, -0.08, 43);
  pc.rect(bag.x - 2, bag.y - 2, 4, 3, '#fffaf0');
  const bowl = iso(0.22, 0.1, 36);
  pc.ellipse(bowl.x, bowl.y - 6, 6, 5, '#bfe6f5');
  pc.ellipse(bowl.x, bowl.y - 4, 5, 3, '#7fc6e8');
  pc.rect(bowl.x - 1, bowl.y - 6, 3, 2, '#f08a2a');
  pc.px(bowl.x + 2, bowl.y - 5, '#f08a2a');
};

const drawPetBed: FurniDrawer = (pc) => {
  drawCylinder(pc, 0.38, 0, 5, '#d9534f', '#b53f3b');
  const c = iso(0, 0, 5);
  pc.ellipse(c.x, c.y, 17, 8, '#e97a73');
  pc.ellipse(c.x, c.y + 1, 13, 6, '#f6d9c0');
  pc.ellipse(c.x - 3, c.y, 5, 2, '#fff1e2');
  paw(pc, c.x + 5, c.y + 2, '#e3b596');
};

function bowl(pc: PixelCanvas, color: string, fill: string, specks: string): void {
  drawCylinder(pc, 0.2, 0, 5, shade(color, 1.1), color);
  const c = iso(0, 0, 5);
  pc.ellipse(c.x, c.y, 8, 4, shade(color, 0.7));
  pc.ellipse(c.x, c.y + 0.5, 7, 3, fill);
  for (const [dx, dy] of [
    [-3, 0],
    [2, -1],
    [0, 1],
    [4, 1],
  ] as const) {
    pc.px(c.x + dx, c.y + dy, specks);
  }
}

const drawFoodBowl: FurniDrawer = (pc) => {
  bowl(pc, '#d9433b', '#9c6232', '#6b3f1d');
  // A bone decal on the side.
  const s = iso(0.15, 0.15, 2);
  pc.rect(s.x - 2, s.y - 1, 5, 1, '#fffaf0');
};

const drawWaterBowl: FurniDrawer = (pc) => {
  bowl(pc, '#3a4cb0', '#7fc6e8', '#d9f2fb');
};

const drawPetHouse: FurniDrawer = (pc, rotation) => {
  const wall = faces('#c98f58');
  drawBox(pc, box(-0.38, 0.38, -0.38, 0.38, 0, 26, wall));
  // Pitched roof along u: two slopes and gable ends.
  const roof = '#d9433b';
  const ridge = 44;
  const eave = 24;
  const r = 0.44;
  pc.poly([iso(-r, -r, eave), iso(r, -r, eave), iso(r, 0, ridge), iso(-r, 0, ridge)], shade(roof, 1.1));
  pc.poly([iso(-r, 0, ridge), iso(r, 0, ridge), iso(r, r, eave), iso(-r, r, eave)], roof);
  pc.poly([iso(r - 0.06, -0.38, eave + 1), iso(r - 0.06, 0.38, eave + 1), iso(r - 0.06, 0, ridge - 2)], shade('#c98f58', 0.78));
  // Door on the front.
  const face = frontFace(rotation) ?? 'left';
  const at = face === 'left' ? 0.38 : 0.38;
  faceQuad(pc, face, at, -0.14, 0.14, 0, 15, '#3b2a20');
  const top = face === 'left' ? iso(0, at, 15) : iso(at, 0, 15);
  pc.ellipse(top.x, top.y, 4, 2, '#3b2a20');
  // Name plate.
  const plate = face === 'left' ? iso(0, at, 21) : iso(at, 0, 21);
  pc.rect(plate.x - 4, plate.y - 1, 8, 3, '#fffaf0');
};

const drawScratchingPost: FurniDrawer = (pc) => {
  drawBox(pc, box(-0.34, 0.34, -0.34, 0.34, 0, 5, faces('#b8a58a')));
  drawCylinder(pc, 0.09, 5, 40, '#e0c28a', '#cfa968');
  for (let h = 8; h < 40; h += 4) {
    const p = iso(0, 0, h);
    pc.rect(p.x - 6, p.y, 12, 1, '#a88444');
  }
  drawBox(pc, box(-0.24, 0.24, -0.24, 0.24, 40, 45, faces('#b8a58a')));
  // A dangling pom-pom.
  const hang = iso(0.24, 0.1, 40);
  pc.rect(hang.x, hang.y, 1, 8, '#6b4226');
  pc.ellipse(hang.x + 0.5, hang.y + 10, 2.5, 2.5, '#f19bb7');
};

const drawToyBall: FurniDrawer = (pc) => {
  const c = iso(0, 0, 5);
  pc.ellipse(c.x, c.y, 5, 5, '#d9433b');
  pc.rect(c.x - 5, c.y - 1, 10, 2, '#fffaf0');
  pc.px(c.x - 2, c.y - 3, '#f7a6a0');
  pc.px(c.x - 1, c.y - 3, '#f7a6a0');
};

const drawBirdPerch: FurniDrawer = (pc) => {
  drawCylinder(pc, 0.25, 0, 4, '#8a6443', '#6b4a2e');
  drawCylinder(pc, 0.04, 4, 46, '#c98f58', '#a8733f');
  // Crossbar with little cups.
  const a = iso(-0.3, 0.3, 46);
  const b = iso(0.3, -0.3, 46);
  pc.line(a.x, a.y, b.x, b.y, '#8a6443');
  pc.line(a.x, a.y + 1, b.x, b.y + 1, '#6b4a2e');
  pc.ellipse(a.x, a.y + 2, 3, 2, '#3d9e8c');
  pc.ellipse(b.x, b.y + 2, 3, 2, '#f0bf36');
  // Swing hanging below.
  const s = iso(0.1, -0.1, 46);
  pc.line(s.x - 4, s.y, s.x - 4, s.y + 12, '#6b4226');
  pc.line(s.x + 4, s.y, s.x + 4, s.y + 12, '#6b4226');
  pc.rect(s.x - 5, s.y + 12, 11, 2, '#d9433b');
};

const drawHamsterCage: FurniDrawer = (pc) => {
  drawBox(pc, box(-0.4, 0.4, -0.3, 0.3, 0, 7, faces('#f08a2a')));
  // Wheel inside, drawn before the bars.
  const w = iso(0, 0, 18);
  pc.ellipse(w.x, w.y, 8, 9, '#3d9e8c');
  pc.ellipse(w.x, w.y, 6, 7, '#dff2ec');
  pc.line(w.x - 5, w.y, w.x + 5, w.y, '#3d9e8c');
  pc.line(w.x, w.y - 6, w.x, w.y + 6, '#3d9e8c');
  const bedding = iso(0.1, 0.1, 7);
  pc.ellipse(bedding.x - 6, bedding.y, 5, 2, '#f3e2b3');
  // Bars on the two visible sides and a lid.
  const bar = '#a9b0b7';
  for (let s = -0.4; s <= 0.401; s += 0.1) {
    const p0 = iso(s, 0.3, 7);
    const p1 = iso(s, 0.3, 30);
    pc.line(p0.x, p0.y, p1.x, p1.y, bar);
  }
  for (let s = -0.3; s <= 0.301; s += 0.1) {
    const p0 = iso(0.4, s, 7);
    const p1 = iso(0.4, s, 30);
    pc.line(p0.x, p0.y, p1.x, p1.y, bar);
  }
  drawBox(pc, box(-0.4, 0.4, -0.3, 0.3, 30, 33, faces('#f08a2a')));
};

const drawDigSpot: FurniDrawer = (pc) => {
  pc.poly([iso(-0.42, -0.36), iso(0.4, -0.42), iso(0.44, 0.38), iso(-0.36, 0.44)], '#c9a36b');
  diamond(pc, 0.32, '#dcbc85');
  const c = iso(0, 0);
  // A small mound and a sneaky X.
  pc.ellipse(c.x - 6, c.y - 1, 6, 3, '#b58a52');
  pc.ellipse(c.x - 7, c.y - 2, 3, 1, '#e8cf9d');
  pc.line(c.x + 3, c.y - 2, c.x + 9, c.y + 2, '#8a4a2a');
  pc.line(c.x + 3, c.y + 2, c.x + 9, c.y - 2, '#8a4a2a');
  for (const [dx, dy] of [
    [-14, 2],
    [12, -4],
    [2, 6],
  ] as const) {
    pc.px(c.x + dx, c.y + dy, '#8a6443');
  }
};

const drawGrassPatch: FurniDrawer = (pc) => {
  diamond(pc, 0.5, '#6fb04a');
  diamond(pc, 0.4, '#7cbd55');
  for (const [u, v] of [
    [-0.3, -0.1],
    [0.2, -0.3],
    [0.1, 0.25],
    [-0.15, 0.32],
    [0.32, 0.1],
    [-0.02, -0.02],
  ] as const) {
    const p = iso(u, v);
    pc.px(p.x, p.y - 1, '#4f8f34');
    pc.px(p.x + 1, p.y - 2, '#4f8f34');
    pc.px(p.x + 2, p.y - 1, '#4f8f34');
  }
  const flower = iso(0.25, -0.05);
  pc.px(flower.x, flower.y - 1, '#fffaf0');
  pc.px(flower.x + 1, flower.y - 1, '#f0bf36');
};

const drawGoldenBone: FurniDrawer = (pc) => {
  drawBoxes(pc, [
    box(-0.26, 0.26, -0.26, 0.26, 0, 18, faces('#3a4cb0')),
    box(-0.2, 0.2, -0.2, 0.2, 18, 22, faces('#fffaf0')),
  ]);
  const c = iso(0, 0, 32);
  const gold = '#f0bf36';
  const light = '#ffe58a';
  pc.rect(c.x - 7, c.y - 1, 14, 4, gold);
  for (const sx of [-1, 1]) {
    pc.ellipse(c.x + sx * 8, c.y - 1, 3, 3, gold);
    pc.ellipse(c.x + sx * 8, c.y + 3, 3, 3, gold);
  }
  pc.rect(c.x - 6, c.y - 1, 10, 1, light);
  pc.px(c.x - 9, c.y - 2, light);
  // Sparkles.
  pc.px(c.x + 12, c.y - 8, '#fffaf0');
  pc.px(c.x - 12, c.y - 5, '#fffaf0');
  // Stand post from plinth to bone.
  pc.rect(c.x - 1, c.y + 3, 2, 6, '#c9a13a');
};

/** Drawers for pet furniture. Owned by the pets feature. */
export const PET_DRAWERS: Record<string, FurniDrawer> = {
  pet_shop_counter: drawShopCounter,
  pet_bed: drawPetBed,
  food_bowl: drawFoodBowl,
  water_bowl: drawWaterBowl,
  pet_house: drawPetHouse,
  scratching_post: drawScratchingPost,
  toy_ball: drawToyBall,
  bird_perch: drawBirdPerch,
  hamster_cage: drawHamsterCage,
  dig_spot: drawDigSpot,
  grass_patch: drawGrassPatch,
  golden_bone: drawGoldenBone,
};
